/**
 * Licence margin (`/reports/licences`; #1112, #1142; manager and billing). Every Pax8 licence and usage charge against
 * what we bill in Books, on three tabs: Licences, Usage (UBB) and Renewals. The tab lives in the URL (`?tab=`), so each
 * is a link. Until the Pax8 slice answers, `not_connected` / `unavailable` render as words, never as an empty table.
 */
import { EmptyState, ErrorBanner, Tabs, TabsContent, TabsList, TabsTrigger } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { useRouter, useSearch } from '@tanstack/react-router'
import { PlugZap } from 'lucide-react'
import { Frame, PaneHeader } from '../../components/frame'
import type { LicenceMarginReport } from '../../lib/api/generated'
import { isoDay, LICENCE_TABS, type LicenceTab, licencesQuery, parseReportsSearch, type ReportsSearch } from './api'
import { LicencesTab } from './licences/licences-tab'
import { dayLabel, monthLabel, utcDay } from './licences/model'
import { RenewalsTab } from './licences/renewals-tab'
import { UsageTab } from './licences/usage-tab'
import { Breadcrumb } from './report'
import { QueryView, whenLabel } from './shared'

export { RenewalCell, type RenewalChange, renewalChange } from './licences/renewal-card'

const TAB_LABEL: Record<LicenceTab, string> = { licences: 'Licences', usage: 'Usage (UBB)', renewals: 'Renewals' }

/** The as-of day (local calendar day of the sync), as a UTC midnight timestamp the renewal maths counts from. */
export function asOfDay(asOf: string | null | undefined, now: Date = new Date()): number {
  const d = asOf ? new Date(asOf) : now
  return utcDay(isoDay(Number.isNaN(d.getTime()) ? now : d))
}

export function LicencesBody({ report, tab, onTab }: { report: LicenceMarginReport; tab: LicenceTab; onTab: (tab: LicenceTab) => void }) {
  if (report.state === 'not_connected') {
    return (
      <EmptyState
        icon={<PlugZap />}
        title="Licence margin is not connected yet."
        detail={report.note ?? 'It fills in once the Pax8 integration ships and its first sync runs. Nothing here means "no licences".'}
      />
    )
  }
  if (report.state === 'unavailable') {
    return (
      <ErrorBanner
        title="Licence margin couldn't load"
        detail={`${report.note ?? 'The Pax8 or Books read failed'}${report.as_of ? `. Last good sync ${whenLabel(report.as_of)}` : ''}. Nothing is shown rather than a figure we cannot stand behind.`}
      />
    )
  }
  const rows = report.rows ?? []
  const usage = report.usage ?? []
  const notes = report.notes ?? {}
  const asOf = asOfDay(report.as_of)
  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-muted-foreground" data-as-of>
        Data read {report.as_of ? dayLabel(new Date(asOf).toISOString()) : 'at an unknown time'} from Pax8 and Books.
        {report.usage_period ? ` Usage is the Pax8 invoice for ${monthLabel(report.usage_period, true)}.` : ''}
        {report.near_pct ? ` "Near" means within ${report.near_pct}%.` : ''}
      </p>
      <Tabs value={tab} onValueChange={(v) => onTab(v as LicenceTab)}>
        <TabsList aria-label="Licence margin views">
          {LICENCE_TABS.map((t) => (
            <TabsTrigger key={t} value={t}>
              {TAB_LABEL[t]}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="licences" className="pt-4">
          <LicencesTab rows={rows} notes={notes} />
        </TabsContent>
        <TabsContent value="usage" className="pt-4">
          <UsageTab rows={usage} notes={notes} months={report.usage_months ?? []} />
        </TabsContent>
        <TabsContent value="renewals" className="pt-4">
          <RenewalsTab rows={rows} notes={notes} asOf={asOf} />
        </TabsContent>
      </Tabs>
      <section className="grid max-w-[80ch] gap-1.5 text-xs text-muted-foreground" aria-labelledby="licence-method">
        <h2 id="licence-method" className="text-sm font-semibold text-foreground">
          How lines are matched
        </h2>
        <p>
          Licences match on the Pax8 SKU carried by the Books item (a SKU written with a term label counts), and on the item name where the
          SKU is missing, mistyped or names a different tier. Customers match on the words in their names. Annual invoices that bill a
          monthly licence as 12 × the monthly rate are converted to per month. Cost now is the price held for the current term; cost at
          renewal is Pax8's current buy rate for the same term.
        </p>
        <p>
          Links: Customer opens the CRM account, Licence opens the customer in Pax8, Billed opens the Books recurring invoice, Books
          customer opens the Books contact. Add a note on any line for a discount you agreed, a reason, or the next step.
        </p>
      </section>
    </div>
  )
}

export function LicencesScreen() {
  const search = useSearch({ strict: false }) as ReportsSearch
  const query = useQuery(licencesQuery(search.brand))
  const router = useRouter()
  const tab: LicenceTab = search.tab ?? 'licences'
  const onTab = (next: LicenceTab) =>
    void router.navigate({
      to: '/reports/$reportId',
      params: { reportId: 'licences' },
      search: parseReportsSearch({ ...search, tab: next === 'licences' ? undefined : next }),
      replace: true,
    })
  return (
    <Frame
      active="reports"
      railless
      main={
        <div className="reports-root flex h-full flex-col overflow-y-auto" data-scroll-top>
          <PaneHeader
            title={<Breadcrumb title="Licence margin" />}
            subtitle="Every Pax8 licence and usage charge against what we bill in Books. Margins are banded: negative, under 5%, under 10%, under 20%."
          />
          <main className="w-full max-w-[1500px] px-4 py-4 sm:px-5">
            <QueryView query={query} what="Licence margin">
              {(report) => <LicencesBody report={report} tab={tab} onTab={onTab} />}
            </QueryView>
          </main>
        </div>
      }
    />
  )
}
